/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PatternFormat from 'react-number-format';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'App/Store';
import { clearSelectedIds } from 'App/Features/ECBulkUpdate';
import { ROLE_RECORD_MENU } from 'Constant/Roles';
import { ECApis } from 'Datasource/EC';
import SpinnerComponent from 'Components/Common/SpinnerComponent';
import useMessage from 'Hooks/useMessage';
import BoxForm from 'Components/Common/Form/BoxForm';
import FormGroupDate from 'Components/Common/Form/FormGroupDate';
import FormGroupInput from 'Components/Common/Form/FormGroupInput';
import FormSelectShopEC from 'Components/Common/Select/FormSelectShopEC';
import MessageError from 'Components/Common/MessageError';
import ECSaleProductDetailSchema from 'Components/Page/EC/Schemas/ECSaleProductDetailSchema';
import ECFormGroupDateRange from 'Components/Page/EC/Form/FormGroupDateRange/ECFormGroupDateRange';
import { normalizeDate } from 'Utils/DateTime';
import { DATE_FORMAT_2, DATE_FORMAT_FOR_DATE_PICKER } from 'Constant/Date';
import { SelectValue } from 'Types/Common';
import { handleEnteringNumber } from 'Utils/Numbers';

interface GroupedProduct {
  artistId: number;
  artistName: string;
  titleId: number;
  titleName: string;
  variationId: number;
  variationName: string;
  products: Array<{
    productId: number;
    productName: string;
    cardId: number;
    urlProductSecret: string;
    publicStartDate: string | null;
    publicEndDate: string | null;
    saleStartDate: string | null;
    saleEndDate: string | null;
  }>;
}

interface Shop {
  id: number | null;
  name: string;
}

interface SubmitFailure {
  id: number;
  message: string;
}

enum designateDrawTypeEnum {
  noLottery = 0,
  hasLottery = 1,
}

enum productSecretTypeEnum {
  noProductSecret = 0,
  hasProductSecret = 1,
}

const EC_SALE_ROLES = [
  ROLE_RECORD_MENU.ECCompMaster,
  ROLE_RECORD_MENU.ECCompManager,
  ROLE_RECORD_MENU.SALEChanelMaster,
  ROLE_RECORD_MENU.SALEChanelManager,
];

const initShop: Shop = { id: null, name: '' };

export default function ECBulkUpdateSaleProducts(): JSX.Element {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { role } = useAppSelector((state) => state.auth);
  const selectedIds = useAppSelector(
    (state) => (state.ecBulkUpdate as any).selectedIds as number[],
  );
  const { openMessageError } = useMessage();

  const [groupedData, setGroupedData] = useState<GroupedProduct[]>([]);
  const [loadingGrouped, setLoadingGrouped] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shop, setShop] = useState<Shop>(initShop);
  const [failures, setFailures] = useState<SubmitFailure[]>([]);
  const [isConfigLotteryPurchase, setIsConfigLotteryPurchase] = useState(false);

  const methods = useForm<any>({
    defaultValues: {
      designateDraw: designateDrawTypeEnum.noLottery,
      drawDate: null,
      isPublic: true,
      maximumDraw: null,
      publicStartDate: null,
      publicEndDate: null,
      saleStartDate: null,
      saleEndDate: null,
      aboveDetailNote: null,
      belowDetailNote: null,
      limitMemberTotalPurchase: null,
      isLimitMemberTotalPurchase: false,
      limitTotalOnetimePurchase: null,
      isLimitTotalOnetimePurchase: false,
      timeBetweenPurchase: null,
      isTimeBetweenPurchase: false,
      isLimitBuyForUser: false,
      isProductSecret: productSecretTypeEnum.noProductSecret,
      domain: '',
      cdId: null,
    },
    resolver: yupResolver(ECSaleProductDetailSchema),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  // role guard
  useEffect(() => {
    if (role && !EC_SALE_ROLES.includes(role)) {
      history.replace('/');
    }
  }, [role, history]);

  // on mount: validate selectedIds then load grouped data
  useEffect(() => {
    if (!selectedIds || selectedIds.length === 0) {
      toast('商品が選択されていません。', { type: 'warning' });
      history.replace('/ec/list-sale-products');
      return;
    }

    const fetchGrouped = async () => {
      try {
        setLoadingGrouped(true);
        const {
          data: { data },
        } = await ECApis.getSaleProductsGroupedByIds(selectedIds);
        setGroupedData(data);
      } catch (error) {
        openMessageError(error);
      } finally {
        setLoadingGrouped(false);
      }
    };

    fetchGrouped();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isDisabledSaleDatePicker = useMemo(() => {
    if (!watch('publicStartDate') && !watch('publicEndDate')) {
      return true;
    }
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('publicStartDate'), watch('publicEndDate')]);

  const maxSaleStartDate = useMemo(() => {
    if (watch('publicEndDate')) {
      const newPublicEndDate = new Date(watch('publicEndDate'));
      const newSaleEndDate = watch('saleEndDate') ? new Date(watch('saleEndDate')) : null;
      if (newSaleEndDate) {
        if (newPublicEndDate < newSaleEndDate) {
          return normalizeDate(watch('publicEndDate'));
        }
      } else {
        return normalizeDate(watch('publicEndDate'));
      }
    }
    return normalizeDate(watch('saleEndDate'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('publicEndDate'), watch('saleEndDate')]);

  const minPublicEndDate = useMemo(() => {
    if (watch('saleEndDate')) {
      if (new Date(watch('saleEndDate')) > new Date(watch('publicStartDate'))) {
        return normalizeDate(watch('saleEndDate'));
      }
    }
    return normalizeDate(watch('publicStartDate'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('saleEndDate'), watch('publicStartDate')]);

  const onChangeDesignateDraw = (value: boolean) => {
    setValue(
      'designateDraw',
      value ? designateDrawTypeEnum.hasLottery : designateDrawTypeEnum.noLottery,
    );
    if (!value) {
      setValue('maximumDraw', null);
      setValue('drawDate', null);
    }
  };

  const onChangeLimitBuyForUser = (value: boolean) => {
    setValue('isLimitBuyForUser', value);
    if (!value) {
      setValue('isLimitMemberTotalPurchase', false);
      setValue('limitMemberTotalPurchase', null);
      setValue('isLimitTotalOnetimePurchase', false);
      setValue('limitTotalOnetimePurchase', null);
      setValue('isTimeBetweenPurchase', false);
      setValue('timeBetweenPurchase', null);
    }
  };

  const onChangeProductSecret = (value: boolean) => {
    setValue(
      'isProductSecret',
      value ? productSecretTypeEnum.hasProductSecret : productSecretTypeEnum.noProductSecret,
    );
    if (!value) {
      setValue('domain', '');
    }
  };

  const onChangeIsLimitMemberTotalPurchase = (value: boolean) => {
    setValue('isLimitMemberTotalPurchase', value);
    if (!value) {
      setValue('limitMemberTotalPurchase', null);
    }
  };

  const onChangeIsLimitTotalOnetimePurchase = (value: boolean) => {
    setValue('isLimitTotalOnetimePurchase', value);
    if (!value) {
      setValue('limitTotalOnetimePurchase', null);
    }
  };

  const onChangeIsTimeBetweenPurchase = (value: boolean) => {
    setValue('isTimeBetweenPurchase', value);
    if (!value) {
      setValue('timeBetweenPurchase', null);
    }
  };

  const handleSelectShop = (item: SelectValue) => {
    setShop({ id: item.value as number, name: item.label as string });
  };

  const handleClearShop = () => {
    setShop(initShop);
  };

  const onSubmit = async (values: any) => {
    try {
      setIsSubmitting(true);
      setFailures([]);

      const baseParams: any = {
        ...values,
        publicStartDate: values?.publicStartDate
          ? dayjs(values.publicStartDate).toISOString()
          : undefined,
        publicEndDate: values?.publicEndDate
          ? dayjs(values.publicEndDate).toISOString()
          : undefined,
        saleStartDate: values?.saleStartDate
          ? dayjs(values.saleStartDate).toISOString()
          : undefined,
        saleEndDate: values?.saleEndDate ? dayjs(values.saleEndDate).toISOString() : undefined,
        drawDate: values?.drawDate ? dayjs(values.drawDate).toISOString() : undefined,
        shopId: shop?.id,
        hasSettingPublic: 1,
      };

      if (!values.limitMemberTotalPurchase || !values.isLimitMemberTotalPurchase) {
        baseParams.limitMemberTotalPurchase = null;
      }
      if (!values.limitTotalOnetimePurchase || !values.isLimitTotalOnetimePurchase) {
        baseParams.limitTotalOnetimePurchase = null;
      }
      if (!values.timeBetweenPurchase || !values.isTimeBetweenPurchase) {
        baseParams.timeBetweenPurchase = null;
      }

      delete baseParams.isLimitMemberTotalPurchase;
      delete baseParams.isLimitTotalOnetimePurchase;
      delete baseParams.isTimeBetweenPurchase;
      delete baseParams.isLimitBuyForUser;
      delete baseParams.cdId;

      const productDataMap = new Map<
        number,
        {
          cardId: number;
          urlProductSecret: string;
          publicStartDate: string | null;
          publicEndDate: string | null;
          saleStartDate: string | null;
          saleEndDate: string | null;
        }
      >(
        groupedData.flatMap((g) =>
          g.products.map((p) => [
            p.productId,
            {
              cardId: p.cardId,
              urlProductSecret: p.urlProductSecret,
              publicStartDate: p.publicStartDate,
              publicEndDate: p.publicEndDate,
              saleStartDate: p.saleStartDate,
              saleEndDate: p.saleEndDate,
            },
          ]),
        ),
      );

      const noPublicTimer = !values.publicStartDate && !values.publicEndDate;
      const noSaleTimer = !values.saleStartDate && !values.saleEndDate;

      const items = selectedIds.map((id) => {
        const productData = productDataMap.get(id);
        return {
          id,
          ...baseParams,
          publicStartDate: noPublicTimer
            ? productData?.publicStartDate ?? undefined
            : baseParams.publicStartDate,
          publicEndDate: noPublicTimer
            ? productData?.publicEndDate ?? undefined
            : baseParams.publicEndDate,
          saleStartDate: noSaleTimer
            ? productData?.saleStartDate ?? undefined
            : baseParams.saleStartDate,
          saleEndDate: noSaleTimer ? productData?.saleEndDate ?? undefined : baseParams.saleEndDate,
          cardId: productData?.cardId,
          urlProductSecret:
            baseParams.isProductSecret === 1 ? productData?.urlProductSecret : undefined,
        };
      });

      const { data } = await ECApis.bulkUpdateSaleProductDetails({ items });

      const successCount: number = data?.data?.success?.length ?? selectedIds.length;
      toast(`${successCount}件の販売設定を一括更新しました。`, { type: 'success' });
      dispatch(clearSelectedIds());
      history.push('/ec/list-sale-products');
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData?.failures && Array.isArray(responseData.failures)) {
        setFailures(responseData.failures);
      } else {
        openMessageError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!role || !EC_SALE_ROLES.includes(role)) {
    return <SpinnerComponent isLoading />;
  }

  return (
    <div className="pt-4 px-4">
      <SpinnerComponent isLoading={loadingGrouped || isSubmitting} />
      <FormProvider {...methods}>
        <Form>
          <BoxForm title="販売設定一括変更">
            <div className="p-2">
              <Row>
                {/* Col 1: 選択した商品 + 販売店舗 + 非公開にする */}
                <Col md="4">
                  <div className="mb-4">
                    <p className="fw-bold mb-2">選択した商品</p>
                    <div
                      style={{
                        minHeight: '200px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                      }}
                    >
                      {groupedData.map((group) => (
                        <div
                          key={`${group.artistId}-${group.titleId}-${group.variationId}`}
                          className="mb-2"
                        >
                          <div
                            className="fw-bold px-2 py-1"
                            style={{ background: '#f0f0f0', borderRadius: '3px' }}
                          >
                            {group.artistName} / {group.titleName} ({group.variationName})
                          </div>
                          <div className="ps-3 pt-1">
                            {group.products.map((c) => (
                              <div key={c.productId}>{c.productName}</div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4 w-100">
                    <Form.Group className="mb-3 d-flex align-items-center">
                      <Form.Label className="fw-bold mb-0 me-3 text-nowrap">販売店舗</Form.Label>
                      <div className="flex-fill">
                        <FormSelectShopEC
                          colMd="12"
                          classLabel="px-0 fw-bold"
                          colClass="px-0"
                          showIcon
                          placeholder="検索"
                          emptyLine
                          defaultValue={shop.name}
                          onSelect={(selectedItem: SelectValue) => handleSelectShop(selectedItem)}
                          onClear={handleClearShop}
                        />
                      </div>
                    </Form.Group>
                  </div>

                  <div className="mb-4">
                    <Form.Check
                      type="checkbox"
                      id="isPublic"
                      name="isPublic"
                      label="非公開にする"
                      className="mb-1 ec_custom-checkbox-red"
                      checked={!watch('isPublic')}
                      onChange={() => setValue('isPublic', !watch('isPublic'))}
                    />
                    <div className="ec_desc-sale_product">
                      <span className="fs-10">
                        ※公開中または販売期間中であってもECサイトには表示されません
                      </span>
                    </div>
                  </div>
                </Col>

                {/* Col 2: ページ公開タイマー + 販売制御タイマー + 抽選購入 */}
                <Col md="4">
                  <div className="mb-4">
                    <ECFormGroupDateRange
                      label="ページ公開タイマー"
                      classForm="mb-0"
                      labelMd="12"
                      colMd="12"
                      classLabel="fw-bold"
                      placeholderStart="開始"
                      placeholderEnd="終了"
                      showTimeSelect
                      startDate={normalizeDate(watch('publicStartDate'))}
                      endDate={normalizeDate(watch('publicEndDate'))}
                      onChangeStartDate={(date: any) => {
                        if (!date) {
                          setValue('publicStartDate', null);
                          setValue('publicEndDate', null);
                        } else {
                          setValue('publicStartDate', dayjs(date).format(DATE_FORMAT_2));
                        }
                      }}
                      onChangeEndDate={(date: any) => {
                        if (!date) {
                          setValue('publicEndDate', null);
                        } else {
                          setValue('publicEndDate', dayjs(date).format(DATE_FORMAT_2));
                        }
                      }}
                      startDateDisabled={false}
                      endDateDisabled={!watch('publicStartDate')}
                      desc="期間中は商品ページがECサイトで公開中となります"
                      minStartDate={new Date()}
                      maxStartDate={normalizeDate(watch('publicEndDate'))}
                      minEndDate={minPublicEndDate}
                      errorStartDate={errors?.publicStartDate?.message}
                      errorEndDate={errors?.publicEndDate?.message}
                    />
                  </div>

                  <div className="mb-5">
                    <ECFormGroupDateRange
                      label="販売制御タイマー"
                      classForm="mb-0"
                      labelMd="12"
                      colMd="12"
                      classLabel="fw-bold"
                      placeholderStart="開始"
                      placeholderEnd="終了"
                      showTimeSelect
                      startDate={normalizeDate(watch('saleStartDate'))}
                      endDate={normalizeDate(watch('saleEndDate'))}
                      onChangeStartDate={(date: any) => {
                        if (!date) {
                          setValue('saleStartDate', null);
                          setValue('saleEndDate', null);
                        } else {
                          setValue('saleStartDate', dayjs(date).format(DATE_FORMAT_2));
                        }
                      }}
                      onChangeEndDate={(date: any) => {
                        if (!date) {
                          setValue('saleEndDate', null);
                        } else {
                          setValue('saleEndDate', dayjs(date).format(DATE_FORMAT_2));
                        }
                        if (watch('drawDate')) {
                          toast('抽選日時をクリアしました。', { type: 'warning' });
                          setValue('drawDate', null);
                        }
                      }}
                      startDateDisabled={isDisabledSaleDatePicker}
                      endDateDisabled={!watch('saleStartDate') || isDisabledSaleDatePicker}
                      desc="期間中はECサイトで購入が可能となります"
                      minStartDate={normalizeDate(watch('publicStartDate'))}
                      maxStartDate={maxSaleStartDate}
                      maxEndDate={normalizeDate(watch('publicEndDate'))}
                      errorStartDate={errors?.saleStartDate?.message}
                      errorEndDate={errors?.saleEndDate?.message}
                    />
                  </div>

                  <div className="ml-10px">
                    <div className="d-flex align-items-center gap-4 mb-1">
                      <Form.Check
                        type="checkbox"
                        name="isConfigLotteryPurchase"
                        id="config_lottery_purchase_1"
                        label={
                          <div className="ec_sale_control_header-sale_product">
                            抽選購入の設定を行う
                          </div>
                        }
                        className="ec_custom-checkbox"
                        checked={isConfigLotteryPurchase}
                        onChange={() => {
                          const next = !isConfigLotteryPurchase;
                          setIsConfigLotteryPurchase(next);
                          onChangeDesignateDraw(next);
                        }}
                      />
                    </div>
                    {isConfigLotteryPurchase &&
                      watch('designateDraw') == designateDrawTypeEnum.hasLottery && (
                        <div className="mt-3">
                          <div>
                            <FormGroupInput
                              value={watch('maximumDraw')}
                              onChange={(value) => {
                                if (value == '') {
                                  setValue('maximumDraw', null);
                                } else {
                                  setValue('maximumDraw', handleEnteringNumber(value) ?? '');
                                }
                              }}
                              labelMd="3"
                              colMd="8"
                              label="上限数"
                              classLabel="lg-fs-12px"
                              errorMessage={errors.maximumDraw?.message}
                            />
                          </div>
                          <div>
                            <FormGroupDate
                              label="抽選日時"
                              classLabel="lg-fs-12px"
                              classForm="mb-5px"
                              colMd="8"
                              labelMd="3"
                              minDate={
                                watch('saleEndDate')
                                  ? normalizeDate(watch('saleEndDate'))
                                  : new Date()
                              }
                              disabled={false}
                              compareMinDate={true}
                              selected={normalizeDate(watch('drawDate'))}
                              onChange={(date: any) => {
                                if (!date) {
                                  setValue('drawDate', null);
                                  return;
                                }
                                setValue('drawDate', dayjs(date).format(DATE_FORMAT_2));
                              }}
                              dateFormat={DATE_FORMAT_FOR_DATE_PICKER}
                              placeholder="yyyy/mm/dd hh:mm"
                              register={register('drawDate')}
                              errorMessage={errors.drawDate?.message}
                            />
                            <p className="ec_sale_control_desc-sale_product ml-12px mt-0">
                              販売制御タイマー終了日時以降に設定いただけます
                            </p>
                          </div>
                        </div>
                      )}
                  </div>
                </Col>

                {/* Col 3: 購入数量制限 + シークレット販売 */}
                <Col md="4">
                  <div>
                    <Form.Check
                      type="checkbox"
                      name="sale_control_check_0"
                      id="sale_control_check_0"
                      label={
                        <div>
                          <div className="ec_sale_control_header-sale_product">
                            ユーザーの購入数量を制限する
                          </div>
                        </div>
                      }
                      className="mb-1 ec_custom-checkbox"
                      checked={watch('isLimitBuyForUser')}
                      onChange={(e) => onChangeLimitBuyForUser(e.target.checked)}
                    />
                    <div className="ec_sale_control_desc-sale_product mb-3 ml-30px">
                      １人のmiimユーザが購入できる数量を制限することができます。
                    </div>
                  </div>

                  {Boolean(watch('isLimitBuyForUser')) && (
                    <div className="ml-30px">
                      <div>
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            name="sale_control_check_1"
                            id="sale_control_check_1"
                            label={
                              <div className="ec_sale_control_check_header-sale_product">
                                １ユーザが購入できる最大数を
                              </div>
                            }
                            className="mb-1 ec_custom-checkbox"
                            checked={watch('isLimitMemberTotalPurchase')}
                            onChange={(e) => onChangeIsLimitMemberTotalPurchase(e.target.checked)}
                          />
                          <div className="d-flex ec_custom-number_format mx-1">
                            <PatternFormat
                              format={'###'}
                              className="form-control rounded position-relative text-center mx-1"
                              value={watch('limitMemberTotalPurchase') ?? ''}
                              onValueChange={({ value }) => {
                                if (+value > 0) {
                                  setValue('limitMemberTotalPurchase', +value);
                                } else {
                                  setValue('limitMemberTotalPurchase', null);
                                }
                              }}
                            />
                          </div>
                          <div className="ec_sale_control_check_header-sale_product">
                            点に制限します。
                          </div>
                        </div>
                        <div className="ec_sale_control_check_desc-sale_product mt-1 ml-32px">
                          「お一人様５点まで」のような設定が可能です
                        </div>
                      </div>

                      {errors?.limitMemberTotalPurchase && (
                        <div>
                          <MessageError message={errors?.limitMemberTotalPurchase?.message} />
                        </div>
                      )}

                      <div>
                        <div className="d-flex mt-4 d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            name="sale_control_check_2"
                            id="sale_control_check_2"
                            label={
                              <div className="ec_sale_control_check_header-sale_product">
                                一度の決済で購入可能な数量を
                              </div>
                            }
                            className="mb-1 ec_custom-checkbox"
                            checked={watch('isLimitTotalOnetimePurchase')}
                            onChange={(e) => onChangeIsLimitTotalOnetimePurchase(e.target.checked)}
                          />
                          <div className="d-flex ec_custom-number_format mx-1">
                            <PatternFormat
                              format="###"
                              className="form-control rounded position-relative text-center mx-1"
                              value={watch('limitTotalOnetimePurchase') ?? ''}
                              onValueChange={({ value }) => {
                                if (+value > 0) {
                                  setValue('limitTotalOnetimePurchase', +value);
                                } else {
                                  setValue('limitTotalOnetimePurchase', null);
                                }
                              }}
                            />
                          </div>
                          <div className="ec_sale_control_check_header-sale_product">
                            点に制限します。
                          </div>
                        </div>
                        <div className="ec_sale_control_check_desc-sale_product mt-1 ml-32px">
                          カートに入れられる数量を制限します
                        </div>
                      </div>

                      {errors?.limitTotalOnetimePurchase && (
                        <div>
                          <MessageError message={errors?.limitTotalOnetimePurchase?.message} />
                        </div>
                      )}

                      <div>
                        <div className="d-flex mt-4 d-flex align-items-center">
                          <Form.Check
                            type="checkbox"
                            name="sale_control_check_3"
                            id="sale_control_check_3"
                            label={
                              <div className="ec_sale_control_check_header-sale_product">
                                前回購入時から
                              </div>
                            }
                            className="mb-1 ec_custom-checkbox"
                            checked={watch('isTimeBetweenPurchase')}
                            onChange={(e) => onChangeIsTimeBetweenPurchase(e.target.checked)}
                          />
                          <div className="d-flex ec_custom-number_format mx-1">
                            <PatternFormat
                              format="###"
                              className="form-control rounded position-relative text-center mx-1"
                              value={watch('timeBetweenPurchase') ?? ''}
                              onValueChange={({ value }) => {
                                if (+value > 0) {
                                  setValue('timeBetweenPurchase', +value);
                                } else {
                                  setValue('timeBetweenPurchase', null);
                                }
                              }}
                            />
                          </div>
                          <div className="ec_sale_control_check_header-sale_product">
                            分間は購入できないよう制限します。
                          </div>
                        </div>
                        <div className="ec_sale_control_check_desc-sale_product mt-1 ml-32px">
                          時間的購入制限を行います
                        </div>
                      </div>

                      {errors?.timeBetweenPurchase && (
                        <div>
                          <MessageError message={errors?.timeBetweenPurchase?.message} />
                        </div>
                      )}
                    </div>
                  )}

                  <div className="border-bottom-ec my-4" />

                  <div>
                    <Form.Check
                      type="checkbox"
                      name="isProductSecret"
                      id="isProductSecret_1"
                      label={
                        <div>
                          <div className="ec_sale_control_header-sale_product">
                            シークレット販売の設定を行う
                          </div>
                        </div>
                      }
                      className="mb-1 ec_custom-checkbox"
                      checked={watch('isProductSecret')}
                      onChange={(e) => onChangeProductSecret(e.target.checked)}
                    />
                    <div className="ec_sale_control_desc-sale_product mb-3 ml-30px">
                      指定ドメインからのアクセスのみ許可します。通常のECサイトには掲載されません。
                    </div>
                  </div>

                  {watch('isProductSecret') === productSecretTypeEnum.hasProductSecret && (
                    <div className="ml-20px">
                      <FormGroupInput
                        label="ドメイン"
                        labelMd="3"
                        colMd="9"
                        classInput="mb-2"
                        register={register('domain')}
                        value={watch('domain')}
                        name="domain"
                        errorMessage={errors.domain?.message}
                        onChange={(value) => {
                          setValue('domain', value);
                        }}
                        classHintText="fs-10"
                        hintText={`複数登録する場合は「,」カンマ区切りで入力してください。\n「https://」は含めないでください。`}
                      />
                    </div>
                  )}
                </Col>
              </Row>

              {failures.length > 0 && (
                <div className="mt-4 p-3 border border-danger rounded">
                  <div className="text-danger fw-bold mb-2">
                    以下の商品の更新に失敗しました（全件ロールバック済み）:
                  </div>
                  <ul className="mb-0">
                    {failures.map((f) => (
                      <li key={f.id} className="text-danger">
                        ID {f.id}: {f.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="text-center mt-4 text-danger ec_fs-15">
                ※ご注意※
                <br />
                選択中の商品はすべて同じ値で上書きされます。
                <br />
                購入制限などご注意ください。
              </div>
              <div className="d-flex justify-content-center mt-3 mb-2">
                <Button style={{ width: 150 }} onClick={handleSubmit(onSubmit)}>
                  保存する
                </Button>
              </div>
            </div>
          </BoxForm>
        </Form>
      </FormProvider>
    </div>
  );
}
